import { HttpClient, JsonpClientBackend } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap, forkJoin } from 'rxjs';
import { observableToBeFn } from 'rxjs/internal/testing/TestScheduler';
import { QuestionHelpText } from '../model/question-help-text';
import { BaseRestApiService } from './baseRestApiService.service';
import { EndPoints, UrlProviderService } from './urlprovider.service';

interface QuestionHelpTextResponse {
    questionHelpTexts: QuestionHelpText[];
  }

@Injectable({
    providedIn: 'root'
})
export class QuestionHelpTextService extends BaseRestApiService {

    private questionHelpTexts: {[questionID: number]: string} = {}; // cache the help texts
    private inProgress: { [questionID: number]: BehaviorSubject<boolean> } = {};  // track in-progress requests

    constructor(http: HttpClient, urlProvider: UrlProviderService) {
        super(http, urlProvider);
    }

    getHelpText(ids: number[]): Observable<QuestionHelpText[]> {
        return this.get<any>(EndPoints.questionHelpText, '', `Batch?ids=${ids.toString()}`).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result['questionHelpTexts'])));
            })
        );
    }

    // this is our new updated function
    getQuestionHelpText2(ids: number[], refreshFlag: boolean = false): Observable<QuestionHelpText[]> {
        
        const cachedResults: QuestionHelpText[] = [];
        const idsToFetch: number[] = [];

        ids.forEach((id) => {
            if (this.questionHelpTexts[id] && !refreshFlag) {
                console.log("caching it")
              const cachedHelpText = this.questionHelpTexts[id];
              cachedResults.push({
                ID: id, // Ensure this matches `QuestionHelpText` structure
                HELP_TEXT: cachedHelpText,
                QUESTIONS_ID: id,
                CREATED_BY: '', // Default value if not cached
                CREATED_DATE: new Date(), // Default value if not cached
                MODIFIED_BY: 'system', // Default value
                DOCUMENT_SECTIONS: [], // Default value
                QUESTION: [], // Default value
                QUESTION_HELP_TEXT_REV: [], // Default value
              });
            } else {
                console.log("just pushing the ids")
              idsToFetch.push(id); // Collect IDs that need to be fetched
            }
          });

        // If all the help texts are already cached, return the cached results
        if (idsToFetch.length === 0) {
            return of(cachedResults);
        }

        // If requests are already in progress for some IDs, avoid re-triggering them
        const inProgressRequests: number[] = [];
        idsToFetch.forEach((id) => {
        if (this.inProgress[id] && this.inProgress[id].getValue()) {
            inProgressRequests.push(id); // Collect IDs that are in progress
        }
        });

         // If any request is in progress, we should wait for those as well
          if (inProgressRequests.length > 0) {
            console.log("length was greater than 0. calling waitForInProgressRequests")
            return this.waitForInProgressRequests(ids).pipe(
                switchMap(() => {
                  const allResults = ids.map((id) => ({
                    ID: id,
                    HELP_TEXT: this.questionHelpTexts[id] || '', 
                    QUESTIONS_ID: id,
                    CREATED_BY: '', // Default 
                    CREATED_DATE: new Date(), // Default 
                    MODIFIED_BY: 'system', // Default 
                    DOCUMENT_SECTIONS: [], // Default 
                    QUESTION: [], // Default 
                    QUESTION_HELP_TEXT_REV: [], // Default
                  }));
                  return of(allResults);
                })
              );
        }

          // Fetch the remaining IDs in a single batch request
          return this.fetchBatchQuestionHelpText2(idsToFetch).pipe(
            tap((results) => {
                console.log("Type of results:", typeof results); 
              // Cache the fetched results
              if (Array.isArray(results))
              {
                results.forEach((result) => {
                    this.questionHelpTexts[result.ID] = result.HELP_TEXT;
                });
              }
              else {
                console.error("Error: 'results' is not an array.");
              }

              console.log("return from fetchBatchQuestionHelpText2 is:")
              console.log(results)
            }),
            catchError((error) => {
              console.error('Error fetching help texts:', error);
              throw error;
            })
     
          );
    }
    getQuestionHelpText(ids: number[], refreshFlag: boolean = false): Observable<{[key:number]: string}> {
        
        const cachedResults: { [key: number]: string } = {};
        const idsToFetch: number[] = [];

        ids.forEach((id) => {
            if (this.questionHelpTexts[id] && !refreshFlag) {
              cachedResults[id] = this.questionHelpTexts[id];
            } else {
              idsToFetch.push(id); // Collect IDs that need to be fetched
            }
          });

        // If all the help texts are already cached, return the cached results
        if (idsToFetch.length === 0) {
            return of(cachedResults);
        }

        // If requests are already in progress for some IDs, avoid re-triggering them
        const inProgressRequests: number[] = [];
        idsToFetch.forEach((id) => {
        if (this.inProgress[id] && this.inProgress[id].getValue()) {
            inProgressRequests.push(id); // Collect IDs that are in progress
        }
        });

         // If any request is in progress, we should wait for those as well
          if (inProgressRequests.length > 0) {
            return this.waitForInProgressRequests(ids).pipe(
            switchMap(() => {
                return of({ ...cachedResults, ...this.questionHelpTexts });
            })
         );
        }

          // Fetch the remaining IDs in a single batch request
        return this.fetchBatchQuestionHelpText(idsToFetch).pipe(
            tap((results) => {
            // Cache the fetched results
            results.forEach((result) => {
                this.questionHelpTexts[result.id] = result.helpText;
            });
            }),
            catchError((error) => {
            console.error('Error fetching help texts:', error);
            throw error;
            }),
            switchMap(() => {
            return of({ ...cachedResults, ...this.questionHelpTexts });
            })
        );
    }
    
    private fetchBatchQuestionHelpText2(ids: number[]):Observable<QuestionHelpText[]> {
        // const queryString = `Batch?ids=${ids.join(',')}`; // Creating query string for batch request
        return this.get<any>(EndPoints.questionHelpText, '', `Batch?ids=${ids.join(',')}`).pipe(
            switchMap((result)  => {
                const questionHelpTexts = (result as { questionHelpTexts: QuestionHelpText[] }).questionHelpTexts;

                // Check if questionHelpTexts is actually an array
                if (Array.isArray(questionHelpTexts)) {
                    return of(questionHelpTexts); // Return the array
                } else {
                    console.error('Expected "questionHelpTexts" array but found:', questionHelpTexts);
                    return of([]); // Return an empty array if not found or not an array
                }
            })
        );
       }

  private fetchBatchQuestionHelpText(ids: number[]): Observable<{ id: number; helpText: string }[]> {
    // const queryString = `Batch?ids=${ids.join(',')}`; // Creating query string for batch request
     return this.get<QuestionHelpTextResponse>(EndPoints.questionHelpText , '' , `Batch?ids=${ids.join(',')}`).pipe(
       map((response) => {
         // Assuming the response contains a 'questionHelpTexts' array with id and helpText
         return response['questionHelpTexts'].map((item: any) => ({
           id: item['QUESTIONS_ID'],
           helpText: item['questionHelpText'],
         }));
       })
     );
   }
 



  private waitForInProgressRequests(ids: number[]): Observable<void> {
    const inProgressObservables: Observable<void>[] = ids.map(
      (id) => this.inProgress[id].asObservable().pipe(switchMap((inProgress) => (inProgress ? of(undefined) : of(void 0))))
    );
    return forkJoin(inProgressObservables).pipe(
        switchMap(() => of<void>(undefined)) // Emit `undefined` when all in-progress requests are done
      );
  }
/*
  return this.get<any>(EndPoints.questionHelpText, '', `Batch?ids=${ids.toString()}`).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result['questionHelpTexts'])));
            })
        );
         */
    
}
